import { computed, Injectable, resource } from '@angular/core';
import { ComponentStoreV2Factory } from '../../../../shared/store/common/component-store-v2.factory';
import { globalStore } from '../../../../shared/shared.global-service';
import {
    selectCurrentService,
    selectCurrentServiceId,
    selectCurrentServiceSettings,
    selectFolder,
    selectFolderId,
    selectServices,
} from '../../../store/person/person.selectors';
import { map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { currentFolderWorkplace, getSharingFolderIds } from '../../../../../utils/folder.utils';
import {
    COMMON_SERVICE_SETTINGS_DEFAULT,
    SERVICE_SETTINGS_CONTACTS_TYPE_KEY,
    SOCIAL_SERVICE_SETTINGS_BILLING_TYPE_KEY,
} from '@libs/types/src/services/types/common-service-settings.type';
import {
    SOCIAL_SERVICE_CONTRACTS_DEFAULT,
    SOCIAL_SERVICE_CONTRACTS_KEY,
} from '@libs/types/src/services/types/social-service-settings-contracts.type';
import {
    SOCIAL_CREDIT_SETTINGS_DEFAULT, SOCIAL_SERVICE_SETTINGS_CREDITS_TYPE_KEY,
} from '@libs/types/src/services/types/social-credit-settings.types';
import { featureOn } from '../../../../../feature-flag';
import { folderCreditInfoFragment } from './person-basic-info.query';
import { ServiceGroupEnum } from '@libs/types/src/services/enum/service-group.enum';

@Injectable()
export class PersonBasicInfoStore extends ComponentStoreV2Factory {

    public currentService = globalStore!.selectSignal(selectCurrentService);
    public currentServiceContractSettings = globalStore!.selectSignal(selectCurrentServiceSettings(SOCIAL_SERVICE_CONTRACTS_KEY, SOCIAL_SERVICE_CONTRACTS_DEFAULT));
    public currentServiceBillingSettings = globalStore!.selectSignal(selectCurrentServiceSettings(
        SOCIAL_SERVICE_SETTINGS_BILLING_TYPE_KEY,
        COMMON_SERVICE_SETTINGS_DEFAULT,
    ));

    public hasMultipleWorkplaces = computed(() => (this.currentService()?.workplaces?.length || 0) > 1);

    public folder = toSignal(this.store!.select(selectFolder));
    public folderId = toSignal(this.store!.select(selectFolderId));
    public serviceId = toSignal(this.store!.select(selectCurrentServiceId));

    public currentWorkplace = computed(() => {
        return currentFolderWorkplace(this.folder());
    });

    public contactsSharingIds = toSignal(globalStore!.select(selectServices).pipe(
        map((services) =>
            getSharingFolderIds(this.serviceId(), services, this.folder()?.relatedFolders, SERVICE_SETTINGS_CONTACTS_TYPE_KEY),
        ),
    ));

    public readonly creditInfo = computed(() =>
        this.creditInfoResource.hasValue() ? this.creditInfoResource.value() : null,
    );

    private readonly creditSettings = globalStore!.selectSignal(selectCurrentServiceSettings(
        SOCIAL_SERVICE_SETTINGS_CREDITS_TYPE_KEY, SOCIAL_CREDIT_SETTINGS_DEFAULT,
    ));
    private readonly creditInfoResource = resource({
        params: () => featureOn.SOCIAL_CREDITS && this.creditSettings().useCard
            ? this.folderId() || undefined : undefined,
        loader: ({ params }) => this.loadCreditInfo(params),
    });

    private async loadCreditInfo(folderId: number) {
        const folder = await this.api.getFolder({ id: folderId }, folderCreditInfoFragment);
        const creditGroup = folder.activeGroups?.find(item => item.group.type === ServiceGroupEnum.CREDITS);
        return {
            groupName: creditGroup?.group.name ?? null,
            balance: folder.creditBalance,
        };
    }

}
